// ==========================
// FILE: src/components/OrganDonation.js
// ==========================
import React, { useEffect, useRef, useState, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
// REMOVED unused: import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox"; // NEW
import { useAccess } from "../access/AccessContext"; // NEW
import "../styles/layout.css";

/* Based on your current file; only surgical changes (no UI restyle). */

const ORGAN_LIST = [
  "Heart","Lungs","Liver","Kidneys","Pancreas","Intestines","Corneas","Skin","Bone/Connective Tissue","Bone Marrow","Tissue",
];

// ---- Helpers (why: export/forward/PDF like E-will) ----
function downloadTextFile(name, content) {
  const blob = new Blob([content || ""], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name || "organ-donation.txt";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
async function loadScript(src) {
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = resolve; s.onerror = () => reject(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });
}
async function ensurePdfLibs() {
  if (!window.html2canvas) {
    await loadScript("https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js");
  }
  if (!window.jspdf && !(window.jsPDF && window.jsPDF.jsPDF)) {
    await loadScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
  }
  const jsPDF = window.jspdf?.jsPDF || window.jsPDF?.jsPDF;
  if (!jsPDF) throw new Error("jsPDF not available");
  return { jsPDF };
}

export default function OrganDonation({ user }) {
  const { readOnly } =
    useAccess() || { readOnly: false };

  const [organs, setOrgans] = useState({});
  const [docId, setDocId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // hidden print root
  const printRootRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "organDonations"), where("uid", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const d = snap.docs[0];
        const data = d.data() || {};
        setDocId(d.id);
        setOrgans(data.organs || {});
      } else {
        setDocId(null);
        setOrgans({});
      }
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  function setChoice(name, value) {
    setOrgans((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    if (!user) return;
    const payload = {
      uid: user.uid,
      organs,
      updatedAt: serverTimestamp(),
      ...(docId ? {} : { createdAt: serverTimestamp() }),
    };
    if (docId) {
      await updateDoc(doc(db, "organDonations", docId), payload);
    } else {
      const ref = await addDoc(collection(db, "organDonations"), payload);
      setDocId(ref.id);
    }
    setEditing(false);
  }

  async function handleDelete() {
    if (!user || !docId) return;
    if (!window.confirm("Delete your organ donation preferences?")) return;
    await deleteDoc(doc(db, "organDonations", docId));
    setDocId(null);
    setOrgans({});
    setEditing(false);
  }

  const brandDark = "#2a0516";
  const okGreen = "#28b528";
  const dangerRed = "#a20000";
  const muted = "#657899";

  // --------- HOISTED HOOK (fix ESLint 'hooks called conditionally') ---------
  const summaryText = useMemo(() => {
    const lines = ["Organ Donation Preferences", ""];
    ORGAN_LIST.forEach((name) => {
      const v = organs?.[name];
      lines.push(`${name}: ${v === "yes" ? "Donate" : v === "no" ? "Do not donate" : "—"}`);
    });
    return lines.join("\n");
  }, [organs]);
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="container"><div style={{ maxWidth: 480, margin: "40px auto", color: "#645155" }}>Loading...</div></div>
    );
  }

  // ---- Header actions ----
  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button type="button" className="btn dp-help read" onClick={() => setShowDrawer(true)} {...(readOnly ? { "data-allow-readonly": "1" } : {})}>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onClick={() => setShowVideo(true)} {...(readOnly ? { "data-allow-readonly": "1" } : {})}>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = () => (
    <div className="dp-help-row">
      <button type="button" className="btn dp-help read" onClick={() => setShowDrawer(true)} {...(readOnly ? { "data-allow-readonly": "1" } : {})}>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onClick={() => setShowVideo(true)} {...(readOnly ? { "data-allow-readonly": "1" } : {})}>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  // ---- PDF download (uses hidden print root) ----
  async function downloadPDF() {
    try {
      const { jsPDF } = await ensurePdfLibs();
      const root = printRootRef.current;
      if (!root) return;

      // make visible just for rendering
      const prevDisplay = root.style.display;
      root.style.display = "block";

      const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      await doc.html(root, {
        margin: 24,
        autoPaging: "text",
        html2canvas: { scale: 0.95, useCORS: true, backgroundColor: "#ffffff" },
        windowWidth: 740,
      });
      doc.save("organ-donation.pdf");

      root.style.display = prevDisplay || "none";
    } catch (err) {
      alert("PDF export failed. You can still use your browser’s Print → Save as PDF.\n" + (err?.message || String(err)));
    }
  }

  // ---- Bottom actions row (same as E-will) shown when NOT editing ----
  function BottomActions() {
    if (editing) return null;
    return (
      <div className="form-actions" style={{ marginTop: 16 }}>
        <button
          className="btn btn-main"
          type="button"
          onClick={downloadPDF}
          {...(readOnly ? { "data-allow-readonly": "1" } : {})}
        >
          Download
        </button>
        <button
          className="btn btn-main"
          type="button"
          onClick={() => downloadTextFile("organ-donation.txt", summaryText)}
          {...(readOnly ? { "data-allow-readonly": "1" } : {})}
        >
          Export
        </button>
        <button
          className="btn btn-main"
          type="button"
          onClick={() => {
            const subject = "Organ Donation Preferences";
            const body = encodeURIComponent(summaryText);
            window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
          }}
          {...(readOnly ? { "data-allow-readonly": "1" } : {})}
        >
          Forward
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header with actions */}
      <div className="header-row">
        <h2>Organ Donation</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
      </div>

      <div className="hero-wrap">
        <img src="/lastwish-account-organdonation.jpg" alt="Organ Donation" className="hero-img" />
      </div>

      <div className="mobile-only" style={{margin: "10px 0"}}>
        <ActionsMobile />
      </div>

      {/* Content card */}
      <div
        style={{
          margin: "20px auto 40px",
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 18px #eee",
          padding: 36,
          maxWidth: 720,
        }}
      >
        {/* VIEW (both roles). Owner sees Edit/Delete; Manager (readOnly) does not. */}
        {!editing ? (
          <>
            <ul style={{listStyle: "none", padding: 0, marginBottom: 20}}>
              {ORGAN_LIST.map((name, idx) => {
                const choice = organs[name];
                const labelColor = choice === "yes" ? okGreen : choice === "no" ? dangerRed : muted;
                const bg = idx % 2 === 1 ? "#f7f7f7" : "transparent";
                return (
                  <li key={name} style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: bg, fontSize: 17}}>
                    <span>{name}</span>
                    <span style={{ color: labelColor }}>
                      {choice === "yes" ? "Donate" : choice === "no" ? "Do not donate" : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Owner-only edit/delete */}
            {!readOnly && (
              <>
                <button onClick={() => setEditing(true)} style={{background: brandDark, color: "#fff", border: "none", borderRadius: 6, padding: "9px 22px", fontWeight: 600, marginRight: 15, cursor: "pointer"}}>
                  {docId ? "Edit Preferences" : "Enter Preference"}
                </button>
                {docId && (
                  <button onClick={handleDelete} style={{background: "#fff", color: dangerRed, border: "1.6px solid #f9c6c6", borderRadius: 6, padding: "9px 18px", fontWeight: 600, cursor: "pointer"}}>Delete</button>
                )}
              </>
            )}

            {/* Bottom actions (Download / Export / Forward) — same as E-will */}
            <BottomActions />
          </>
        ) : (
          // EDITOR (owner only)
          <form onSubmit={handleSave}>
            <div style={{ display: "grid", gap: 12 }}>
              {ORGAN_LIST.map((name, idx) => {
                const choice = organs[name] || "";
                return (
                  <div key={name} style={{display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center", padding: "8px 12px", background: idx % 2 === 1 ? "#f7f7f7" : "#fff", border: "1px solid #e9dff0", borderRadius: 10}}>
                    <div style={{ fontWeight: 600, color: brandDark }}>{name}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setChoice(name, "yes")} style={{background: choice === "yes" ? "#ebffe9" : "#fff", color: choice === "yes" ? okGreen : brandDark, border: choice === "yes" ? "1.2px solid #b9e5b9" : "1.2px solid #dac8e8", borderRadius: 6, padding: "6px 10px", fontWeight: 600, cursor: "pointer"}}>Donate</button>
                      <button type="button" onClick={() => setChoice(name, "no")} style={{background: choice === "no" ? "#ffeef0" : "#fff", color: choice === "no" ? dangerRed : brandDark, border: choice === "no" ? "1.2px solid #f0cfd3" : "1.2px solid #dac8e8", borderRadius: 6, padding: "6px 10px", fontWeight: 600, cursor: "pointer"}}>Do not donate</button>
                      <button type="button" onClick={() => setChoice(name, "")} style={{background: choice === "" ? "#f7f7f7" : "#fff", color: brandDark, border: "1.2px solid #dac8e8", borderRadius: 6, padding: "6px 10px", fontWeight: 600, cursor: "pointer"}}>Clear</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
              <button type="submit" style={{background: brandDark, color: "#fff", border: "none", borderRadius: 6, padding: "9px 22px", fontWeight: 600, cursor: "pointer"}}>Save</button>
              <button type="button" onClick={() => setEditing(false)} style={{background: "#fff", color: brandDark, border: "1.2px solid #dac8e8", borderRadius: 6, padding: "9px 18px", fontWeight: 600, cursor: "pointer"}}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* Hidden print root used for PDF */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, opacity: 0, display: "none" }} aria-hidden>
        <div ref={printRootRef} style={{ width: 740 }}>
          <h2 style={{ margin: 0, padding: 0 }}>Organ Donation Preferences</h2>
          <p style={{ color: "#657899" }}>
            Generated: {new Date().toLocaleString()}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12pt" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid " }}>Organ / Tissue</th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid " }}>Preference</th>
              </tr>
            </thead>
            <tbody>
              {ORGAN_LIST.map((name, idx) => {
                const val = organs?.[name];
                const pref = val === "yes" ? "Donate" : val === "no" ? "Do not donate" : "—";
                return (
                  <tr key={`p-${idx}`}>
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid #f4f4f4" }}>{name}</td>
                    <td style={{ padding: "6px 8px", borderBottom: "1px solid #f4f4f4" }}>{pref}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* NEW popup video player (replaces old iframe modal) */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://www.youtube.com/watch?v=yZrTzXhDetI"
        title="How the Organ Donation page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* Right drawer (unchanged) */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Organ Donation page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" {...(readOnly ? { "data-allow-readonly": "1" } : {})}>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" {...(readOnly ? { "data-allow-readonly": "1" } : {})}>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Record your donation preferences for each organ/tissue. Choose <b>Donate</b>, <b>Do not donate</b>, or leave blank if undecided, then <b>Save</b>.</p>
              <ol>
                <li>Review the list of organs and tissues.</li>
                <li>Select your preference on each row.</li>
                <li>Save your choices. You can return any time to update them.</li>
              </ol>
              <p><i>Note:</i> This stores your preferences for loved ones and executors. Legal donor registry steps may still be required in your country.</p>
            </div>
          </aside>
        </div>
      )}

      {/* Scoped styles (unchanged look) */}
      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}

        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }

        .desktop-only { display: none; }
        .mobile-only { display: block; }
        @media (min-width: 900px){ .desktop-only { display:block } .mobile-only { display:none } }

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }

        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; animation: dp-slide-in .22s ease-out both; }
        @keyframes dp-slide-in { from { transform: translateX(20px); opacity:0; } to { transform: translateX(0); opacity:1; } }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
      `}</style>
    </div>
  );
}
